import * as React from 'react';

/** Selectors for an open dropdown/listbox where Enter should pick an option
 * rather than submit the surrounding modal. */
const OPEN_LISTBOX_SELECTOR = '[role="listbox"], [role="option"], [role="menu"], [role="menuitem"]';

/**
 * Returns an `onKeyDown` handler that submits a modal form on Enter. It first
 * blurs the focused input so commit-on-blur / debounced values are flushed,
 * then calls `submit`. Ignores Enter in a `<textarea>` or while a dropdown is
 * open so multiline input and option selection keep working.
 */
export function useEnterSubmit(
  submit: () => void
): (event: React.KeyboardEvent<HTMLElement>) => void {
  const submitRef = React.useRef(submit);

  React.useLayoutEffect(() => {
    submitRef.current = submit;
  }, [submit]);
  return React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter') return;
    if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return;

    const target = event.target as HTMLElement | null;
    if (target?.tagName?.toUpperCase() === 'TEXTAREA') return;
    // Enter inside an open dropdown/listbox selects an option — don't submit.
    if (target?.closest(OPEN_LISTBOX_SELECTOR)) return;
    if (target?.getAttribute('aria-expanded') === 'true') return;

    event.preventDefault();
    event.stopPropagation();

    // Commit any commit-on-blur / debounced input buffer before reading state.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    setTimeout(() => submitRef.current(), 0);
  }, []);
}
